import React, { useRef, useState } from 'react';

import { CapabilityLockedBanner } from '../CapabilityLockedBanner';

import type { DocumentationGatedUploaderProps } from './types';

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * DocumentationGatedUploader — file upload + capability gate.
 *
 * Pattern DS v2.2.0 — primitivo universal para upload com gate.
 * Usado em Brief 5 R4 + Brief 3 MitigateAlertModal evidence[].
 *
 * Se hasCapability=false, renderiza CapabilityLockedBanner (D-B defense-in-depth).
 * Suporta drag & drop + click to upload + file list com remove.
 *
 * Acessibilidade:
 * - role="button" no dropzone + aria-describedby
 * - Input file visível a screen readers
 */
export function DocumentationGatedUploader({
  requiredCapability,
  hasCapability,
  onFilesSelected,
  accept = '*',
  maxFileSizeBytes = DEFAULT_MAX_SIZE,
  maxFiles = 1,
  label,
  description,
  allowDragDrop = true,
  uploadedFiles = [],
  onFileRemove,
  disabled = false,
}: DocumentationGatedUploaderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // D-B defense-in-depth: gate parent blocks entire uploader
  if (!hasCapability) {
    return (
      <CapabilityLockedBanner
        capability={requiredCapability}
        fallbackMessage="Seu perfil não tem permissão para enviar documentos."
      />
    );
  }

  const validateFiles = (fileList: FileList | File[]): { valid: File[]; errors: string[] } => {
    const valid: File[] = [];
    const errs: string[] = [];
    const files = Array.from(fileList);

    if (files.length > maxFiles) {
      errs.push(`Máximo de ${maxFiles} arquivo(s) permitido(s).`);
      return { valid, errors: errs };
    }

    for (const file of files) {
      if (file.size > maxFileSizeBytes) {
        errs.push(`"${file.name}" excede o tamanho máximo (${formatBytes(maxFileSizeBytes)}).`);
      } else {
        valid.push(file);
      }
    }

    return { valid, errors: errs };
  };

  const handleFiles = (fileList: FileList | File[]): void => {
    const { valid, errors: errs } = validateFiles(fileList);
    setErrors(errs);
    if (valid.length > 0) {
      onFilesSelected(valid);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragOver(false);
    if (!allowDragDrop || disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const dropzoneStyle: React.CSSProperties = {
    border: `2px dashed ${isDragOver ? 'var(--brand, #d97706)' : 'var(--border-default, #e5e7eb)'}`,
    borderRadius: 'var(--radius-lg, 9px)',
    padding: 'var(--space-6, 24px)',
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: isDragOver ? 'var(--brand-subtle, #fef3c7)' : 'var(--bg-surface, #fff)',
    opacity: disabled ? 0.5 : 1,
    transition: 'background 150ms ease, border-color 150ms ease',
  };

  return (
    <div
      style={{ fontFamily: 'var(--font-sans, system-ui)' }}
      data-testid="documentation-gated-uploader"
    >
      {label && (
        <p
          style={{
            fontSize: 'var(--text-sm, 12px)',
            fontWeight: 'var(--fw-medium, 500)',
            color: 'var(--fg-1, #111)',
            marginBottom: 'var(--space-1, 4px)',
          }}
        >
          {label}
        </p>
      )}
      {description && (
        <p
          style={{
            fontSize: 'var(--text-xs, 11px)',
            color: 'var(--fg-3, #6b7280)',
            marginBottom: 'var(--space-3, 12px)',
          }}
        >
          {description}
        </p>
      )}

      {/* Drop zone */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`Área de upload${label ? ` — ${label}` : ''}`}
        style={dropzoneStyle}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
        }}
        onDragOver={
          allowDragDrop && !disabled
            ? (e) => {
                e.preventDefault();
                setIsDragOver(true);
              }
            : undefined
        }
        onDragLeave={allowDragDrop ? () => setIsDragOver(false) : undefined}
        onDrop={allowDragDrop ? handleDrop : undefined}
        data-testid="uploader-dropzone"
      >
        <p
          style={{
            fontSize: 'var(--text-sm, 12px)',
            color: 'var(--fg-2, #374151)',
            marginBottom: 'var(--space-1, 4px)',
          }}
        >
          Clique para selecionar{allowDragDrop ? ' ou arraste os arquivos aqui' : ''}
        </p>
        <p style={{ fontSize: 'var(--text-xs, 11px)', color: 'var(--fg-3, #6b7280)' }}>
          Máximo: {formatBytes(maxFileSizeBytes)} · Até {maxFiles} arquivo(s)
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={maxFiles > 1}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          // Reset to allow selecting same file again
          e.target.value = '';
        }}
        data-testid="uploader-input"
        aria-hidden="true"
      />

      {/* Errors */}
      {errors.length > 0 && (
        <ul
          role="alert"
          style={{
            marginTop: 'var(--space-2, 8px)',
            padding: 0,
            listStyle: 'none',
          }}
          data-testid="uploader-errors"
        >
          {errors.map((err, i) => (
            <li
              key={i}
              style={{
                fontSize: 'var(--text-xs, 11px)',
                color: 'var(--danger-fg, #dc2626)',
              }}
            >
              {err}
            </li>
          ))}
        </ul>
      )}

      {/* Uploaded files list */}
      {uploadedFiles.length > 0 && (
        <ul
          style={{ marginTop: 'var(--space-3, 12px)', padding: 0, listStyle: 'none' }}
          data-testid="uploader-file-list"
        >
          {uploadedFiles.map((file) => (
            <li
              key={file.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2, 8px)',
                padding: 'var(--space-2, 8px) var(--space-3, 12px)',
                background: 'var(--bg-subtle, #f9fafb)',
                borderRadius: 'var(--radius-sm, 5px)',
                marginBottom: 'var(--space-1, 4px)',
                fontSize: 'var(--text-sm, 12px)',
              }}
              data-testid={`uploader-file-${file.id}`}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.name}
              </span>
              <span
                style={{
                  color: 'var(--fg-3, #6b7280)',
                  fontSize: 'var(--text-xs, 11px)',
                  flexShrink: 0,
                }}
              >
                {formatBytes(file.sizeBytes)}
              </span>
              {onFileRemove && (
                <button
                  type="button"
                  onClick={() => onFileRemove(file.id)}
                  aria-label={`Remover ${file.name}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--fg-3, #6b7280)',
                    padding: '0 var(--space-1, 4px)',
                    fontSize: 12,
                  }}
                  data-testid={`uploader-remove-${file.id}`}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
